import React, { useCallback, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, TextField } from '@mui/material'

import './ContactForm.scss'

export default function ContactForm() {
  const [contactFormData, setContactFormData] = useState({ name: '', email: '', phone: '', message: '' })
  const [contactFormErrors, setContactFormErrors] = useState({ name: '', email: '', phone: '', message: '' })

  // status: 'idle' | 'submitting' | 'success' | 'error'
  const [status, setStatus] = useState('idle')
  const [submitError, setSubmitError] = useState('')

  const isSubmitting = status === 'submitting'
  const isError = status === 'error'
  const isSuccess = status === 'success'
  const showSubmitMessage = isError || isSuccess

  const submitMessageText = useMemo(() => {
    if (isError) return submitError || 'There was a problem sending your message. Please try again.'
    if (isSuccess) return 'Message sent!'
    return ''
  }, [isError, isSuccess, submitError])

  const validateFormData = useCallback((data) => {
    const errors = { name: '', email: '', phone: '', message: '' }

    if (!data.email) errors.email = 'Email Required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'Invalid email format'

    if (!data.name) errors.name = 'Name Required'

    if (data.phone && !/^\+?[\d\s\-().]{7,}$/.test(data.phone)) errors.phone = 'Invalid phone number format'

    if (!data.message) errors.message = 'Message Required'

    return errors
  }, [])

  const sendEmail = useCallback(async () => {
    setSubmitError('')
    setStatus('idle') // reset before validation so stale success/error is cleared

    const errors = validateFormData(contactFormData)
    setContactFormErrors(errors)

    const hasErrors = Object.values(errors).some((v) => v && v.trim() !== '')
    if (hasErrors) return

    setStatus('submitting')

    try {
      let ipAddress = null
      try {
        ipAddress = await getIP()
      } catch (ipErr) {
        console.warn('Could not retrieve user IP (ipify). Proceeding without it.', ipErr)
      }

      const response = await fetch('https://worksmart-contact.azurewebsites.net/api/SendEmails', {
        method: 'POST',
        body: JSON.stringify({ ...contactFormData, ip: ipAddress }),
        headers: { 'Content-Type': 'application/json' },
      })

      let responseJson = null
      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        try {
          responseJson = await response.json()
        } catch {
          // ignore parse errors
        }
      }

      if (!response.ok) {
        if (response.status === 400 && responseJson?.errors) {
          setContactFormErrors((prev) => ({ ...prev, ...responseJson.errors }))
          setStatus('error')
          setSubmitError('Please fix the highlighted fields and try again.')
          return
        }

        const serverMsg = responseJson?.error || 'There was a problem sending your message.'
        throw new Error(serverMsg)
      }

      setStatus('success')
    } catch (error) {
      console.error('There was an error submitting the form', error)
      setStatus('error')
      setSubmitError(error?.message || 'There was an error submitting the form.')
    }
  }, [contactFormData, validateFormData])

  return (
    <div id="contact-form-container">
      <div className="contact-form-text">
        <h2>Contact Us</h2>
        <p>Send us a message if your business needs computer or networking support</p>
      </div>

      <Box className="form-control-container">
        {/* Use Box as the <form> and use sx to lock layout (prevents CSS override issues) */}
        <Box
          component="form"
          className="contact-form"
          onSubmit={(e) => {
            e.preventDefault()
            sendEmail()
          }}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            minWidth: 0,
          }}
        >
          <TextField
            fullWidth
            error={!!contactFormErrors.name}
            helperText={contactFormErrors.name}
            id="input-field-name"
            label="Name..."
            variant="standard"
            value={contactFormData.name}
            onChange={(e) => {
              setContactFormData((prev) => ({ ...prev, name: e.target.value }))
              if (contactFormErrors.name) setContactFormErrors((prev) => ({ ...prev, name: '' }))
            }}
          />

          <TextField
            fullWidth
            error={!!contactFormErrors.email}
            helperText={contactFormErrors.email}
            id="input-field-email"
            label="Email..."
            variant="standard"
            value={contactFormData.email}
            onChange={(e) => {
              setContactFormData((prev) => ({ ...prev, email: e.target.value }))
              if (contactFormErrors.email) setContactFormErrors((prev) => ({ ...prev, email: '' }))
            }}
          />

          <TextField
            fullWidth
            error={!!contactFormErrors.phone}
            helperText={contactFormErrors.phone}
            id="input-field-phone"
            label="Phone..."
            variant="standard"
            value={contactFormData.phone}
            onChange={(e) => {
              setContactFormData((prev) => ({ ...prev, phone: e.target.value }))
              if (contactFormErrors.phone) setContactFormErrors((prev) => ({ ...prev, phone: '' }))
            }}
          />

          <TextField
            fullWidth
            error={!!contactFormErrors.message}
            helperText={contactFormErrors.message}
            placeholder="Your message..."
            value={contactFormData.message}
            onChange={(e) => {
              setContactFormData((prev) => ({ ...prev, message: e.target.value }))
              if (contactFormErrors.message) setContactFormErrors((prev) => ({ ...prev, message: '' }))
            }}
            multiline
            rows={10}
          />

          {/* Button row always right-aligned */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Box>

          {/* Status banner BELOW the button, full-width */}
          {showSubmitMessage && (
            <Box
              className={`contact-form-status ${isError ? 'error' : 'success'}`}
              role={isError ? 'alert' : 'status'}
              aria-live={isError ? 'assertive' : 'polite'}
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <p>{submitMessageText}</p>
            </Box>
          )}
        </Box>
      </Box>
    </div>
  )
}

const getIP = async () => {
  const res = await fetch('https://api.ipify.org/?format=json', { method: 'GET' })
  if (!res.ok) throw new Error('Failed to fetch IP address')
  const data = await res.json()
  return data.ip
}
